import { Sidebar } from '../Sidebar';

let activeInspirationListing = null;

export const setActiveInspirationListing = listing => {
    activeInspirationListing = listing || null;
};

export const getActiveInspirationListing = () => {
    if (
        activeInspirationListing?.inspirationSidebar?.content?.querySelector(
            '[data-inspiration-sidebar]'
        )
    ) {
        return activeInspirationListing;
    }

    if (
        window.mdaInspirationsPage?.inspirationSidebar?.content?.querySelector(
            '[data-inspiration-sidebar]'
        )
    ) {
        return window.mdaInspirationsPage;
    }

    return null;
};

const downloadBase64Pdf = (base64File, filename) => {
    const byteCharacters = atob(base64File);
    const byteNumbers = Array.from(byteCharacters, char => char.charCodeAt(0));
    const blob = new Blob([new Uint8Array(byteNumbers)], {
        type: 'application/pdf',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const initInspirationSidebar = listing => {
    if (listing.inspirationSidebar) {
        return;
    }

    listing.inspirationSidebar = new Sidebar({
        overlayClassNames: ['bg-grey-900/60'],
    });
};

export const fetchInspirationDetail = async (listing, termId) => {
    if (typeof ajax === 'undefined') {
        return null;
    }

    const normalizedTermId = Number(termId);

    if (!Number.isFinite(normalizedTermId) || normalizedTermId <= 0) {
        return null;
    }

    const formData = new FormData();
    formData.append('action', 'mda_inspiration_sidebar');
    formData.append('nonce', ajax.nonce);
    formData.append('term_id', String(normalizedTermId));

    try {
        const response = await fetch(ajax.url, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin',
        });

        if (!response.ok) {
            return null;
        }

        const result = await response.json();

        if (!result?.success || typeof result?.data?.html !== 'string') {
            return null;
        }

        return {
            html: result.data.html.trim(),
        };
    } catch {
        return null;
    }
};

export const applyInspirationSidebarHtml = (listing, html) => {
    if (!listing.inspirationSidebar?.content) {
        return null;
    }

    listing.inspirationSidebar.content.innerHTML = html;

    const root = listing.inspirationSidebar.content.querySelector(
        '[data-inspiration-sidebar]'
    );

    if (root && typeof window.mdaMinicart?.initSidebarEnhancements === 'function') {
        window.mdaMinicart.initSidebarEnhancements(root);
    }

    const galleryEl = root?.querySelector('.js-inspiration-gallery');

    if (galleryEl && typeof window.mdaInitInspirationGallery === 'function') {
        window.mdaInitInspirationGallery(galleryEl);
    }

    syncInspirationAddToCartButton(listing);

    return root;
};

const getInspirationSidebarRoot = listing =>
    listing?.inspirationSidebar?.content?.querySelector('[data-inspiration-sidebar]')
    || document.querySelector('[data-inspiration-sidebar]');

const inspirationListHasRemainingAddButtons = root =>
    Boolean(
        root?.querySelector('[data-inspiration-shopping-list] [data-minicart-add-recommendation]')
    );

const inspirationAllRowsAreInCart = root => {
    const rows = root?.querySelectorAll(
        '[data-inspiration-shopping-list] [data-minicart-recommendation]'
    );

    if (!rows || rows.length === 0) {
        return false;
    }

    return Array.from(rows).every(
        row =>
            row.querySelector('[data-minicart-remove-item]')
            && !row.querySelector('[data-minicart-add-recommendation]')
    );
};

const resolveAllProductsInCart = (listing, allProductsInCart = false) => {
    if (allProductsInCart) {
        return true;
    }

    const root = getInspirationSidebarRoot(listing);

    if (!root) {
        return false;
    }

    if (inspirationAllRowsAreInCart(root)) {
        return true;
    }

    return !inspirationListHasRemainingAddButtons(root);
};

export const syncInspirationAddToCartButton = (listing, allProductsInCart = false) => {
    const button =
        listing?.inspirationSidebar?.content?.querySelector(
            '[data-inspiration-add-all-to-cart]'
        )
        || document.querySelector(
            '[data-inspiration-sidebar] [data-inspiration-add-all-to-cart]'
        );

    if (!button || button.getAttribute('aria-busy') === 'true') {
        return;
    }

    const shouldDisable = resolveAllProductsInCart(listing, allProductsInCart);

    button.disabled = shouldDisable;

    if (shouldDisable) {
        button.setAttribute('aria-disabled', 'true');
    } else {
        button.removeAttribute('aria-disabled');
    }
};

export const fetchInspirationShoppingList = async (listing, termId) => {
    if (typeof ajax === 'undefined') {
        return null;
    }

    const normalizedTermId = Number(termId);

    if (!Number.isFinite(normalizedTermId) || normalizedTermId <= 0) {
        return null;
    }

    const formData = new FormData();
    formData.append('action', 'mda_inspiration_shopping_list');
    formData.append('nonce', ajax.nonce);
    formData.append('term_id', String(normalizedTermId));

    try {
        const response = await fetch(ajax.url, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin',
        });

        if (!response.ok) {
            return null;
        }

        const result = await response.json();

        if (!result?.success || typeof result?.data?.html !== 'string') {
            return null;
        }

        return {
            html: result.data.html.trim(),
            allProductsInCart: Boolean(result?.data?.all_products_in_cart),
        };
    } catch {
        return null;
    }
};

export const applyInspirationShoppingListHtml = (listing, html, allProductsInCart = false) => {
    if (typeof html !== 'string' || html.trim() === '') {
        return;
    }

    const currentRoot =
        listing?.inspirationSidebar?.content?.querySelector('[data-inspiration-sidebar]')
        || document.querySelector('[data-inspiration-sidebar]');
    const currentList = currentRoot?.querySelector('[data-inspiration-shopping-list]');

    if (!currentRoot || !currentList) {
        return;
    }

    const temp = document.createElement('div');
    temp.innerHTML = html.trim();

    const nextList = temp.querySelector('[data-inspiration-shopping-list]');

    if (nextList) {
        currentList.replaceWith(nextList);

        if (typeof window.mdaMinicart?.initSidebarEnhancements === 'function') {
            window.mdaMinicart.initSidebarEnhancements(currentRoot);
        }
    }

    syncInspirationAddToCartButton(listing, allProductsInCart);
};

export const syncInspirationShoppingList = async (listing, termId) => {
    const normalizedTermId = Number(termId || listing?.activeInspirationTermId || 0);

    if (normalizedTermId <= 0 || !listing?.inspirationSidebar?.content) {
        return;
    }

    const data = await fetchInspirationShoppingList(listing, normalizedTermId);

    if (!data?.html) {
        return;
    }

    applyInspirationShoppingListHtml(listing, data.html, data.allProductsInCart);
};

export const refreshInspirationShoppingList = async listing => {
    const targetListing = listing || getActiveInspirationListing();
    const termId = Number(targetListing?.activeInspirationTermId || 0);

    if (termId <= 0 || !targetListing?.inspirationSidebar?.content) {
        return;
    }

    await syncInspirationShoppingList(targetListing, termId);
};

export const addInspirationAllToCart = async (listing, termId) => {
    if (typeof ajax === 'undefined') {
        return;
    }

    const normalizedTermId = Number(termId);

    if (!Number.isFinite(normalizedTermId) || normalizedTermId <= 0) {
        return;
    }

    const button =
        listing?.inspirationSidebar?.content?.querySelector(
            '[data-inspiration-add-all-to-cart]'
        )
        || document.querySelector(
            '[data-inspiration-sidebar] [data-inspiration-add-all-to-cart]'
        );

    if (
        button &&
        (button.disabled || button.getAttribute('aria-disabled') === 'true')
    ) {
        return;
    }

    if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
    }

    let allProductsInCart = false;

    const formData = new FormData();
    formData.append('action', 'mda_inspiration_add_all_to_cart');
    formData.append('nonce', ajax.nonce);
    formData.append('term_id', String(normalizedTermId));

    try {
        const response = await fetch(ajax.url, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin',
        });

        if (!response.ok) {
            throw new Error('Request failed');
        }

        const result = await response.json();

        if (!result?.success) {
            throw new Error(
                result?.data?.message ||
                    'Nie udało się dodać produktów do koszyka.'
            );
        }

        const html =
            typeof result?.data?.html === 'string'
                ? result.data.html.trim()
                : '';

        if (html) {
            applyInspirationShoppingListHtml(
                listing,
                html,
                Boolean(result?.data?.all_products_in_cart)
            );
        } else {
            await syncInspirationShoppingList(listing, normalizedTermId);
        }

        allProductsInCart = Boolean(result?.data?.all_products_in_cart);

        const minicartHtml =
            typeof result?.data?.minicart_html === 'string'
                ? result.data.minicart_html.trim()
                : '';
        const addedCartItemKeys = Array.isArray(
            result?.data?.added_cart_item_keys
        )
            ? result.data.added_cart_item_keys.filter(Boolean)
            : [];
        const highlightCartItemKey =
            result?.data?.cart_item_key || addedCartItemKeys[0] || '';

        if (minicartHtml && typeof window.mdaMinicart?.openAfterAdd === 'function') {
            await window.mdaMinicart.openAfterAdd(minicartHtml, {
                highlightCartItemKey,
                sourceProductId: window.mdaMinicart.getPageProductId?.() || 0,
            });
        } else if (typeof window.mdaMinicart?.notifyCartChanged === 'function') {
            window.mdaMinicart.notifyCartChanged();
        } else if (typeof window.mdaMinicart?.markCartDirty === 'function') {
            window.mdaMinicart.markCartDirty();
        }

        const failed = Array.isArray(result?.data?.result?.failed)
            ? result.data.result.failed
            : [];

        if (failed.length > 0) {
            window.alert(
                `Nie udało się dodać: ${failed.join(', ')}`
            );
        }
    } catch (error) {
        console.error(error);
        window.alert(
            error?.message || 'Nie udało się dodać produktów do koszyka.'
        );
    } finally {
        if (button) {
            button.removeAttribute('aria-busy');
        }

        syncInspirationAddToCartButton(listing, allProductsInCart);
    }
};

export const exportInspirationShoppingListPdf = async (listing, termId) => {
    if (typeof ajax === 'undefined') {
        return;
    }

    const normalizedTermId = Number(termId);

    if (!Number.isFinite(normalizedTermId) || normalizedTermId <= 0) {
        return;
    }

    listing.loader?.show();

    const formData = new FormData();
    formData.append('action', 'mda_inspiration_export_pdf');
    formData.append('nonce', ajax.nonce);
    formData.append('term_id', String(normalizedTermId));

    try {
        const response = await fetch(ajax.url, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin',
        });

        if (!response.ok) {
            throw new Error('Request failed');
        }

        const result = await response.json();

        if (!result?.success || typeof result?.data?.file !== 'string') {
            throw new Error(
                result?.data?.message ||
                    'Nie udało się wygenerować pliku PDF.'
            );
        }

        downloadBase64Pdf(result.data.file, `${Date.now()}.pdf`);
    } catch (error) {
        console.error(error);
        window.alert(
            error?.message || 'Nie udało się wygenerować pliku PDF.'
        );
    } finally {
        listing.loader?.hide();
    }
};

export const openInspirationSidebar = async (listing, termId) => {
    initInspirationSidebar(listing);

    if (!listing.inspirationSidebar?.content) {
        return;
    }

    listing.activeInspirationTermId = termId;
    setActiveInspirationListing(listing);

    const detail = await fetchInspirationDetail(listing, termId);
    const html = typeof detail?.html === 'string' ? detail.html.trim() : '';

    if (!html) {
        return;
    }

    applyInspirationSidebarHtml(listing, html);
    await syncInspirationShoppingList(listing, termId);
    listing.inspirationSidebar.open();
};
